/**
 * @rynora/contracts — assertion-kinds catalogue.
 *
 * Phase 2 of the no-code "Verify" UX expansion. The runner (mvp's
 * `flow-executor.ts`) has 8 new Playwright-backed verbs alongside the
 * existing `assert_visible` / `assert_text` / `assert_url`. From the
 * UI's point of view a "Verify" step is a single step whose
 * `fields.assertion` discriminator picks which runner verb the
 * expander in `mvp/handlers/run-handler.ts` will produce.
 *
 * The codes here are the wire-format identifiers — stable, lower-
 * kebab-case, and shared between:
 *
 *   - `StepCard.tsx`'s assertion-kind <select>
 *   - `build-basic-suggestions.ts`'s chip emitter
 *   - `run-handler.ts`'s assert expander (`case 'assert'`)
 *   - any future per-kind picker / migration script
 *
 * Human-readable labels live in `@rynora/messages` (assertions.ts) —
 * everything in this file is mechanical/structural.
 */

/**
 * The set of assertion kinds the UI can ask the runner to execute.
 *
 * Adding a new kind:
 *   1. Add the id to this union.
 *   2. Add a row to `ASSERTION_KIND_DEFS` with the right value-field
 *      affordance + matching runner action key.
 *   3. Add a label in `@rynora/messages/src/assertions.ts`.
 *   4. Add the expander branch in
 *      `packages/mvp/src/handlers/run-handler.ts` `case 'assert'`.
 *   5. If it needs a new runner verb, add it to the `FlowAction`
 *      union in `packages/mvp/src/types.ts` and implement it in
 *      `packages/mvp/src/flow-executor.ts`.
 */
export type AssertionKind =
  | 'visible'
  | 'hidden'
  | 'text-contains'
  | 'text-equals'
  | 'value-equals'
  | 'enabled'
  | 'disabled'
  | 'checked'
  | 'unchecked'
  | 'count-equals'
  | 'collection-contains-all'
  | 'collection-exact-match'
  | 'collection-exact-match-ordered'
  | 'collection-exact-match-multiset'
  | 'collection-selected-option-equals'
  | 'collection-selected-options-exact-match';

/**
 * Per-kind metadata used by the UI to decide how to render the
 * "Verify" step card.
 *
 *   - `requiresValue`  → when true the editor's value field is a
 *                        required input; when false the field is
 *                        hidden and `step.value` is left empty.
 *
 *   - `valueLabelKey`  → key into `@rynora/messages` so the value
 *                        field can swap label / placeholder based on
 *                        the selected kind (e.g. "Expected text"
 *                        for text-contains, "Expected count" for
 *                        count-equals).
 *
 *   - `runnerAction`   → the FlowAction the expander produces.
 *                        Kept here for documentation; the expander
 *                        owns the actual switch.
 *
 *   - `selectorBased`  → false for text-contains / text-equals which
 *                        match against page text rather than a
 *                        resolved locator. Used by the chip builder
 *                        to skip emitting chips for those kinds when
 *                        the source element has no text payload.
 */
export interface AssertionKindDef {
  id: AssertionKind;
  requiresValue: boolean;
  valueLabelKey:
    | null
    | 'expectedText'
    | 'expectedValue'
    | 'expectedCount'
    | 'expectedValues'
    | 'expectedSelectedOption';
  runnerAction:
    | 'assert_visible'
    | 'assert_hidden'
    | 'assert_text'
    | 'assert_text_equals'
    | 'assert_value'
    | 'assert_enabled'
    | 'assert_disabled'
    | 'assert_checked'
    | 'assert_unchecked'
    | 'assert_count'
    | 'assert_collection_contains_all'
    | 'assert_collection_exact_match'
    | 'assert_collection_exact_match_ordered'
    | 'assert_collection_exact_match_multiset'
    | 'assert_collection_selected_option_equals'
    | 'assert_collection_selected_options_exact_match';
  selectorBased: boolean;
}

export const ASSERTION_KIND_DEFS: readonly AssertionKindDef[] = [
  { id: 'visible', requiresValue: false, valueLabelKey: null, runnerAction: 'assert_visible', selectorBased: true },
  { id: 'hidden', requiresValue: false, valueLabelKey: null, runnerAction: 'assert_hidden', selectorBased: true },
  {
    id: 'text-contains',
    requiresValue: true,
    valueLabelKey: 'expectedText',
    runnerAction: 'assert_text',
    selectorBased: false,
  },
  {
    id: 'text-equals',
    requiresValue: true,
    valueLabelKey: 'expectedText',
    runnerAction: 'assert_text_equals',
    selectorBased: false,
  },
  {
    id: 'value-equals',
    requiresValue: true,
    valueLabelKey: 'expectedValue',
    runnerAction: 'assert_value',
    selectorBased: true,
  },
  { id: 'enabled', requiresValue: false, valueLabelKey: null, runnerAction: 'assert_enabled', selectorBased: true },
  { id: 'disabled', requiresValue: false, valueLabelKey: null, runnerAction: 'assert_disabled', selectorBased: true },
  { id: 'checked', requiresValue: false, valueLabelKey: null, runnerAction: 'assert_checked', selectorBased: true },
  { id: 'unchecked', requiresValue: false, valueLabelKey: null, runnerAction: 'assert_unchecked', selectorBased: true },
  {
    id: 'count-equals',
    requiresValue: true,
    valueLabelKey: 'expectedCount',
    runnerAction: 'assert_count',
    selectorBased: true,
  },
  {
    id: 'collection-contains-all',
    requiresValue: true,
    valueLabelKey: 'expectedValues',
    runnerAction: 'assert_collection_contains_all',
    selectorBased: true,
  },
  {
    id: 'collection-exact-match',
    requiresValue: true,
    valueLabelKey: 'expectedValues',
    runnerAction: 'assert_collection_exact_match',
    selectorBased: true,
  },
  {
    id: 'collection-exact-match-ordered',
    requiresValue: true,
    valueLabelKey: 'expectedValues',
    runnerAction: 'assert_collection_exact_match_ordered',
    selectorBased: true,
  },
  {
    id: 'collection-exact-match-multiset',
    requiresValue: true,
    valueLabelKey: 'expectedValues',
    runnerAction: 'assert_collection_exact_match_multiset',
    selectorBased: true,
  },
  {
    id: 'collection-selected-option-equals',
    requiresValue: true,
    valueLabelKey: 'expectedSelectedOption',
    runnerAction: 'assert_collection_selected_option_equals',
    selectorBased: true,
  },
  {
    id: 'collection-selected-options-exact-match',
    requiresValue: true,
    valueLabelKey: 'expectedValues',
    runnerAction: 'assert_collection_selected_options_exact_match',
    selectorBased: true,
  },
] as const;

const KIND_INDEX: Record<AssertionKind, AssertionKindDef> = ASSERTION_KIND_DEFS.reduce(
  (acc, def) => {
    acc[def.id] = def;
    return acc;
  },
  {} as Record<AssertionKind, AssertionKindDef>,
);

export function getAssertionKindDef(id: AssertionKind | string | undefined | null): AssertionKindDef {
  if (id && (id as AssertionKind) in KIND_INDEX) {
    return KIND_INDEX[id as AssertionKind];
  }
  return KIND_INDEX.visible;
}

export function isAssertionKind(id: unknown): id is AssertionKind {
  return typeof id === 'string' && id in KIND_INDEX;
}

/**
 * Back-compat resolver matching the runtime expander's fallback
 * (run-handler.ts `case 'assert'`). When a step was authored before
 * Phase 2 it has no `fields.assertion` — the UI infers the kind from
 * the presence of a value so the assertion-kind <select> shows the
 * right preselected entry instead of always defaulting to `visible`.
 */
export function inferAssertionKind(args: { assertionField?: string | null; value?: string | null }): AssertionKind {
  const raw = (args.assertionField || '').toString().trim().toLowerCase();
  if (isAssertionKind(raw)) return raw;
  return args.value ? 'text-contains' : 'visible';
}
